import React from "react";
import ContactForm from "../../../components/pages/Contact/ContactForm";
import { MapPin, Clock, Mail, Phone } from "lucide-react";
import FAQSection from "../../../components/pages/Contact/FAQAccordion";
import SEO from "../../../components/pages/SEO/SEO";
import contactBg from '../../../assets/lien-he-voi-chung-toi.jpg';

export default function ContactPage() {

  // ✅ Cấu hình SEO cho trang Liên hệ
  const seoData = {
    title: "Liên hệ Mỹ Diệp Property - Sàn giao dịch bất động sản",
    description: "Liên hệ với chúng tôi để được tư vấn mua bán bất động sản nhanh chóng và chuyên nghiệp.",
    keywords: "liên hệ, Real Estate, bất động sản, tư vấn, mua bán, cho thuê, Mỹ Diệp Property",
    image: "/images/og-default.jpg",
    type: "website",
    url: typeof window !== "undefined" ? window.location.href : undefined,
    schema: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Liên hệ Mỹ Diệp Property",
      "description": "Liên hệ với Real Estate Mỹ Diệp Property để nhận tư vấn mua bán, cho thuê bất động sản nhanh chóng và chuyên nghiệp.",
      "url": typeof window !== "undefined" ? window.location.href : "https://vinhomesdemo.com/contact",
      "publisher": {
        "@type": "Organization",
        "name": "Mỹ Diệp Property",
        "logo": {
          "@type": "ImageObject",
          "url": "/images/logo.png"
        }
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+84908065117",
          "contactType": "customer support",
          "areaServed": "VN",
          "availableLanguage": ["Vietnamese"]
        },
        {
          "@type": "ContactPoint",
          "email": "support@realestate.vn",
          "contactType": "customer support"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-[#082f49] text-white">

      {/* 🚀 SEO */}
      <SEO {...seoData} />

      {/* HERO SECTION */}
      <section className="flex flex-col justify-center items-center text-center py-20 md:py-28 bg-[#07253a] relative overflow-hidden" style={{ backgroundImage: `url(${contactBg})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-[#082f49] to-transparent opacity-30"></div>

        <div className="relative z-10">
          <h1 className="text-4xl md:text-6xl font-extrabold uppercase tracking-wider font-serif">
            Liên hệ với <span className="text-[#c9a063] drop-shadow-lg">chúng tôi</span>
          </h1>
          <div className="flex gap-2 items-center mt-4 text-sm text-slate-400">
            <span className="text-white font-medium">Trang chủ</span>
            <span className="text-white font-medium">/</span>
            <span className="text-[#c9a063] font-medium">Liên hệ</span>
          </div>
        </div>
      </section>

      {/* FORM + INFO SECTION */}
      <section className="flex flex-col md:flex-row justify-between items-stretch px-6 md:px-24 py-20 gap-16">
        <div className="w-full md:w-1/2 bg-[#0b3a5c] rounded-3xl shadow-2xl border border-[#1a5b82] p-10 transform hover:shadow-xl transition duration-300">
          <h2 className="text-3xl font-bold mb-8 text-[#c9a063] border-b border-[#c9a063]/30 pb-3">
            Gửi tin nhắn cho chúng tôi
          </h2>
          <ContactForm />
        </div>

        <div className="w-full md:w-1/2 flex flex-col gap-8">
          <h2 className="text-3xl font-bold text-[#c9a063] border-b border-[#c9a063]/30 pb-3 mb-4">
            Thông tin liên hệ
          </h2>
          <p className="text-slate-300 leading-relaxed text-lg">
            Chúng tôi luôn sẵn sàng hỗ trợ bạn. Đừng ngần ngại liên hệ qua hotline, email hoặc ghé thăm văn phòng của chúng tôi để nhận được tư vấn nhanh chóng và chuyên nghiệp.
          </p>

          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-[#0b3a5c] border border-[#1a5b82]">
              <Phone className="w-7 h-7 text-[#c9a063] mt-1 flex-shrink-0" />
              <div>
                <span className="text-slate-200 block text-sm">Hotline & Zalo</span>
                <span className="font-bold text-xl text-white tracking-wider hover:text-[#c9a063] transition">
                  0902 487 996
                </span>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-[#0b3a5c] border border-[#1a5b82]">
              <Mail className="w-7 h-7 text-[#c9a063] mt-1 flex-shrink-0" />
              <div>
                <span className="text-slate-200 block text-sm">Hòm thư hỗ trợ</span>
                <span className="font-bold text-xl text-white hover:text-[#c9a063] transition">
                  mydiepproperties@gmail.com
                </span>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-[#0b3a5c] border border-[#1a5b82]">
              <MapPin className="w-7 h-7 text-[#c9a063] mt-1 flex-shrink-0" />
              <div>
                <span className="text-slate-200 block text-sm">Địa chỉ văn phòng</span>
                <span className="font-medium text-white">158 Cao Đức Lân, Quận 12, TP.HCM</span>
              </div>
            </div>
          </div>

          <div className="mt-4 p-6 bg-[#0b3a5c] rounded-3xl border border-[#1a5b82] shadow-lg">
            <div className="flex items-center gap-3 mb-4 border-b border-[#c9a063]/30 pb-3">
              <Clock className="w-6 h-6 text-[#c9a063]" />
              <h3 className="text-xl font-semibold text-[#c9a063]">Giờ làm việc</h3>
            </div>
            <ul className="space-y-2 text-slate-300">
              <li className="flex justify-between">
                <span>Thứ Hai - Thứ Sáu:</span>
                <span className="font-medium text-white">8:00 - 17:30</span>
              </li>
              <li className="flex justify-between">
                <span>Thứ Bảy:</span>
                <span className="font-medium text-white">8:00 - 12:00</span>
              </li>
              <li className="flex justify-between">
                <span>Chủ Nhật:</span>
                <span className="text-red-400 font-medium">Đóng cửa</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* MAP SECTION */}
      <section className="px-6 md:px-24 py-16 bg-[#07253a]">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#c9a063] mb-3 font-serif">
            Ghé thăm Văn phòng của Chúng tôi
          </h2>
          <p className="text-slate-400 text-lg">
            Tìm đường đến địa điểm giao dịch chính thức của Mỹ Diệp Property
          </p>
        </div>

        <div className="relative w-full h-[450px] md:h-[550px] rounded-3xl overflow-hidden shadow-2xl shadow-[#1a5b82]/50 border-4 border-[#c9a063] transition duration-500 hover:border-[#ffffff] group">

          {/* Lớp phủ gradient và biểu tượng định vị để tăng tính thẩm mỹ */}
          <div className="absolute inset-0 bg-black/10 z-10 pointer-events-none flex justify-center items-center opacity-0 group-hover:opacity-100 transition duration-500">
            <MapPin className="w-16 h-16 text-[#c9a063] animate-pulse" />
          </div>

          <iframe
            title="Vị trí văn phòng Mỹ Diệp Property"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.4996489069146!2d106.7305!3d10.7891!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752ef0a1234567%3A0xabcdef1234567890!2s158%20Cao%20%C4%90%E1%BB%A7c%20L%C3%A2n%2C%20An%20Ph%C3%BA%2C%20Qu%E1%BA%ADn%202%2C%20TP.HCM!5e0!3m2!1svi!2s"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />

          {/* Tagline ở góc bản đồ - ĐÃ CẬP NHẬT ĐỊA CHỈ */}
          <div className="absolute bottom-4 left-4 p-2 bg-[#0b3a5c]/80 rounded-lg backdrop-blur-sm z-20 border border-[#c9a063]">
            <p className="text-sm font-semibold text-[#c9a063]">
              <MapPin className="w-4 h-4 inline mr-1" /> 158 Cao Đức Lân, Phường An Phú, Quận 2, TP.HCM
            </p>
          </div>
        </div>
      </section>
      {/* FAQ SECTION */}
      <FAQSection />
    </div>
  );
}
